import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { LEADS } from "../../constants/testIds";
import { FormField, isFieldValid } from "../common/FormField";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { toast } from "../ui/sonner";

const SOURCES = [
  { value: "walk_in", label: "Walk-in" },
  { value: "phone", label: "Phone Enquiry" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "referral", label: "Referral" },
  { value: "website", label: "Website" },
  { value: "other", label: "Other" },
];

// Shared by both the Desk (`/desk/leads`) and Owner Cockpit (`/app/leads`) Lead modules — the
// "data points to capture" from the product's reference screen: contact info, source, the plan
// they're interested in, a follow-up date/time, and a free-text note. A branch picker only
// appears when the caller didn't already pin one down (Front Desk/Manager are always
// branch-scoped; the Owner Cockpit passes "all" when no specific branch is selected up top).
export function LeadCaptureForm({ open, onOpenChange, branchId, branches }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ branch_id: branchId !== "all" ? branchId : "", name: "", phone: "", email: "", source: "walk_in", plan_interest: "", follow_up_at: "", note: "" });
  const needsBranchPicker = !branchId || branchId === "all";

  const plansQ = useQuery({ queryKey: ["desk-plans"], queryFn: async () => (await api.get("/plans")).data.plans, enabled: open });

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  // Only running campaigns are offered — a finished one shouldn't keep collecting new leads.
  const campaignsQ = useQuery({
    queryKey: ["lead-source-campaigns"],
    queryFn: async () => (await api.get("/marketing/campaigns")).data.campaigns
      .filter((c) => ["active", "paused", "draft"].includes(c.status)),
    // A role without marketing access simply gets no campaign options, never an error.
    retry: false,
  });

  const reset = () => setForm({ branch_id: branchId !== "all" ? branchId : "", name: "", phone: "", email: "", source: "walk_in", plan_interest: "", follow_up_at: "", note: "" });

  const captureMutation = useMutation({
    mutationFn: () => api.post("/leads", {
      branch_id: form.branch_id,
      contact: { name: form.name, phone: form.phone, email: form.email || null },
      source: form.source,
      plan_interest: form.plan_interest || null,
      follow_up_at: form.follow_up_at ? new Date(form.follow_up_at).toISOString() : null,
      note: form.note || null,
    }),
    onSuccess: () => {
      toast.success(`${form.name} added to the pipeline.`);
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      reset();
      onOpenChange(false);
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const canSubmit = form.branch_id && isFieldValid("text", true, form.name) && isFieldValid("tel", true, form.phone);

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="glass-modal max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Capture a new lead</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {needsBranchPicker && (
            <div className="space-y-1.5">
              <Label>Branch <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
              <Select value={form.branch_id} onValueChange={set("branch_id")}>
                <SelectTrigger data-testid={LEADS.captureBranchSelect}><SelectValue placeholder="Choose a branch" /></SelectTrigger>
                <SelectContent>{branches?.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <FormField label="Name" required testId={LEADS.captureNameInput} value={form.name} onChange={set("name")} />
          <FormField label="Phone" type="tel" required testId={LEADS.capturePhoneInput} value={form.phone} onChange={set("phone")} />
          <FormField label="Email" type="email" testId={LEADS.captureEmailInput} value={form.email} onChange={set("email")} />
          <div className="space-y-1.5">
            <Label>Source</Label>
            <Select value={form.source} onValueChange={set("source")}>
              <SelectTrigger data-testid={LEADS.captureSourceSelect}><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                {/* Live ad campaigns, so a lead that came from an ad can be credited to it — this
                    is what closes the loop through to the Revenue & ROI tab. */}
                {(campaignsQ.data || []).map((c) => (
                  <SelectItem key={c.id} value={c.lead_source_tag}>Ad: {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Plan interest</Label>
            <Select value={form.plan_interest} onValueChange={set("plan_interest")}>
              <SelectTrigger data-testid={LEADS.capturePlanSelect}><SelectValue placeholder="Not sure yet" /></SelectTrigger>
              <SelectContent>{plansQ.data?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <FormField label="Follow-up date & time" type="datetime-local" testId={LEADS.captureFollowUpInput} value={form.follow_up_at} onChange={set("follow_up_at")} />
          <div className="space-y-1.5">
            <Label>Enquiry note</Label>
            <Textarea data-testid={LEADS.captureNoteInput} value={form.note} onChange={(e) => set("note")(e.target.value)} placeholder="What are they looking for?" rows={3} />
          </div>
          <Button
            data-testid={LEADS.captureSubmitButton} disabled={!canSubmit || captureMutation.isPending}
            onClick={() => captureMutation.mutate()} className="w-full text-white" style={{ background: "var(--scarlet-accent, #D6202B)" }}
          >
            {captureMutation.isPending ? "Adding…" : "Add to pipeline"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
